/* eslint-disable @typescript-eslint/no-unused-vars */
import {ERecommendationsTypes, IRecommendationsResponse} from '../IRecommendationsMgr';
import ManagerRepository from 'coreClasses/ManagerRepository';
import ProductModel from 'coreClasses/product/ProductModel';
import {EEventSource, EEventType} from 'coreClasses/events/IEvents';
import {IEventData} from 'coreClasses/events/IEvents';
import Observable from 'coreClasses/patterns/observable/Observable';
import AbstractSearchProvider from 'coreClasses/search/providers/AbstractSearchProvider';

/**
 * Represents RecommendationsProvider
 */
abstract class RecommendationsProvider extends Observable {
	private id: string;
	protected managerRepository: ManagerRepository;
	coveredPages: Array<number>;

	/**
	 * @constructor
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 * @param {string} id - id
	 */
	constructor(managerRepository: ManagerRepository, id: string) {
		super();
		this.id = id;
		this.managerRepository = managerRepository;
		this.coveredPages = [
			EEventSource.PDP,
			EEventSource.PLP,
			EEventSource.SEARCH_RESULTS
		];
	}

	abstract getRecommendations(
		type: ERecommendationsTypes,
		products: ProductModel[],
		removeCurrentProduct?: boolean
	): Promise<IRecommendationsResponse>;
	// TODO: abstract getZoneRecommendations()

	/**
	 * Returns active search manager instance if available
	 * @returns {AbstractSearchProvider}
	 */
	private getSearchInstance(): AbstractSearchProvider {
		if (this.managerRepository.getFlyoutsMgr().isFlyoutOpened('quickSearch')) {
			return this.managerRepository.getSearchMgr().getInstance('search-flyout');
		} else {
			return this.managerRepository.getSearchMgr().getInstance();
		}
	}

	/**
	 * Sends a view product event to einstein
	 * @param {ProductModel} product - product
	 */
	sendViewProduct(product: ProductModel, args?: any): void {};

	/**
	 * Sends a add to cart event to einstein
	 * @param {object} productItem - product
	 * @param {ProductModel} product - product
	 * @param {any} args - arguments
	 */
	sendAddToCart(productItem: Object, product: ProductModel): void {};

	/**
	 * Sends a view recommendation event to einstein
	 * @param {IRecommendationsResponse} recommenderDetails - recommendations details
	 * @param {any} args - arguments
	 */
	sendViewReco(recommenderDetails: IRecommendationsResponse, args?: any): void {};

	/**
	 * Sends a click recommendation event to einstein
	 * @param {IRecommendationsResponse} recommenderDetails - recommendations details
	 * @param {ProductModel} product - product
	 * @param {any} args - arguments
	 */
	sendClickReco(recommenderDetails: IRecommendationsResponse, product: ProductModel, args?: any): void {};

	/**
	 * Sends a search view event
	 * @param {string} searchText - search query
	 * @param {object[]} searchResults - products search results
	 * @param {any} args - arguments
	 */
	sendViewSearch(searchText: string, searchResults: object[], args?: any): void {};

	/**
	 * Sends a category view event
	 * @param {string} category - category ID
	 * @param {object[]} searchResults - products search results
	 * @param {any} args - arguments
	 */
	sendViewCategory(category: object, searchResults: ProductModel[], args?: any): void {};

	/**
	 * Sends a click event for product in category
	 * @param {object} category - search query
	 * @param {ProductModel} product - products search results
	 * @param {any} args - arguments
	 */
	sendClickCategory(category: object, product: ProductModel, args?: any): void {};

	/**
	 * Sends a click event for product in search
	 * @param {string} searchText - search query
	 * @param {ProductModel} product - products search results
	 * @param {any} args - arguments
	 */
	sendClickSearch(searchText: string, product: ProductModel, args?: any): void {};

	/**
	 * Sends a page view event
	 * @param {string} path - current url
	 * @param {any} args - arguments
	 */
	sendViewPage(path: string, args?: any): void {};

	/**
	 * Tracks analytics event
	 * @param {EEventType} eventType - event type
	 * @param {IEventData} eventData - event data
	 */
	update(eventType: EEventType, eventData: IEventData): void {
		switch (eventType) {
			case EEventType.PRODUCT_DETAIL_VIEW:
				this.sendViewProduct(eventData.productData.product);

				break;
			case EEventType.PRODUCT_ADD_TO_CART:
				this.sendAddToCart(eventData.productItem, eventData.productData.product);

				break;
			case EEventType.RECOMMENDATIONS_VIEW:
				this.sendViewReco(eventData.recommenderDetails);

				break;
			case EEventType.PRODUCT_RECOMMENDATION_CLICK:
				this.sendClickReco(eventData.recommenderDetails, eventData.product);

				break;
			case EEventType.PRODUCT_LIST_LOAD:
				if (eventData.source !== EEventSource.CONTENTFUL_CATEGORY) {
					if (eventData.searchQuery) {
						this.sendViewSearch(eventData.searchQuery, {
							...eventData.searchResult,
							hits: eventData.productsData.map(({product}) => product)
						});
					} else if (eventData.sourceSpecification) {
						this.sendViewCategory({id: eventData.sourceSpecification}, {
							...eventData.searchResult,
							hits: eventData.productsData.map(({product}) => product)
						});
					}
				}

				break;
			case EEventType.PRODUCT_CLICK:
				if (eventData.source === EEventSource.SEARCH_RESULTS) {
					if (this.getSearchInstance().getSearchQuery()) {
						this.sendClickSearch(this.getSearchInstance().getSearchQuery(), eventData.productData.product);
					} else {
						this.sendClickCategory({id: eventData.sourceSpecification}, eventData.productData.product);
					}
				}

				break;
			case EEventType.PAGE_VIEW:
			case EEventType.SPLASH_PAGE_VIEW:
				if (this.coveredPages.indexOf(eventData.pageType) === -1) {
					this.sendViewPage(eventData.uri);
				}

				break;
		}
	}
}

export default RecommendationsProvider;
